import { useEffect, useRef, memo, useCallback, useState } from "react";
import { useTheme } from "@/hooks/use-theme";
import { Skeleton } from "@/components/ui/skeleton";

interface TradingViewChartProps {
  symbol: string;
  interval?: string;
  height?: number;
  studies?: string[];
  title?: string;
}

// Track if the TradingView script has been loaded globally
let tvScriptLoaded = false;
let tvScriptPromise: Promise<void> | null = null;

function loadTVScript(): Promise<void> {
  if (tvScriptLoaded && (window as any).TradingView) {
    return Promise.resolve();
  }
  if (tvScriptPromise) return tvScriptPromise;

  tvScriptPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/tv.js";
    script.async = true;
    script.onload = () => {
      tvScriptLoaded = true;
      resolve();
    };
    document.head.appendChild(script);
  });
  return tvScriptPromise;
}

const TradingViewChart = memo(({ symbol, interval = "W", height = 500, studies = [], title }: TradingViewChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);

  const resolvedTheme = theme === "dark" ? "dark" : theme === "light" ? "light"
    : (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");

  const widgetId = `tv-chart-${symbol.replace(/[^a-zA-Z0-9]/g, "")}-${interval}`;

  const createWidget = useCallback(() => {
    if (!(window as any).TradingView || !containerRef.current) return;

    containerRef.current.innerHTML = "";

    new (window as any).TradingView.widget({
      container_id: widgetId,
      autosize: true,
      symbol,
      interval,
      timezone: "Europe/Zurich",
      theme: resolvedTheme,
      style: "1",
      locale: "en",
      toolbar_bg: resolvedTheme === "dark" ? "#1e1e2e" : "#f1f3f6",
      enable_publishing: false,
      allow_symbol_change: true,
      save_image: false,
      studies: [
        "RSI@tv-basicstudies",
        ...studies,
      ],
      hide_side_toolbar: false,
      withdateranges: true,
      details: true,
      hotlist: false,
      calendar: false,
    });

    setTimeout(() => setLoading(false), 1500);
  }, [symbol, interval, resolvedTheme, widgetId, studies]);

  useEffect(() => {
    setLoading(true);
    loadTVScript().then(createWidget);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [createWidget]);

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wider">{title}</h3>
      )}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-10 flex flex-col gap-3 p-4 rounded-lg bg-background">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="flex-1 min-h-[200px]" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        <div
          id={widgetId}
          ref={containerRef}
          style={{ height: `${height}px`, width: "100%" }}
          className="rounded-lg overflow-hidden"
        />
      </div>
    </div>
  );
});

TradingViewChart.displayName = "TradingViewChart";

export default TradingViewChart;
